import React, {useEffect, useState} from 'react';
import ReactModal from 'react-modal';
import moment from 'moment';

import {DSelect, HCheckbox, HDatePicker, HInput, HRadio, HookForm} from '../../../validation/HForm';
import {createCouponScheme} from '../../../validation/scheme/couponScheme';
import {Message, SearchFacets} from '..';
import CouponRowsTable from './CouponRowsTable';

import './modalBody.scss';

import CrossIcon from '../../../assets/icons/cross.svg';
import {useSelector} from "react-redux";
import {couponType, couponAnnuityType, defaultCategories, generateCouponTypes} from "../../../helpers/data-constants";
import {cashBoxService} from "../../../services/cashbox-service";
import {messages} from "../../../helpers/messages";
import { getCouponType } from '../modal.utils';
import { useScrollToTop } from '../../../helpers/hooks/useScrollTop';

let startOfToday = moment().utcOffset(0, true).startOf('day').toISOString().substr(0, 10);

const emptyRows = [{
    startPrice: '0',
    endPrice: '15',
    basePurity: '',
    delayPurity: '',
    baseThings: '',
    delayThings: '',
    baseCars: '',
    delayCars: '',
    startDate: startOfToday,
    endDate: '',
},];

const methods = {
    convertCouponRow(couponRow, isHybridCoupon = false) {
        let row = {
            startPrice: couponRow.startPrice,
            endPrice: couponRow.endPrice,
            couponRowName: couponRow.couponRowName,
            basePercentBonus: couponRow.basePercentBonus,
            basePercentBirthday: couponRow.basePercentBirthday,
            baseAnnuityPercentBirthday: couponRow.baseAnnuityPercentBirthday,
            baseAnnuityPercentBonus: couponRow.baseAnnuityPercentBonus,
            minAnnuityDuration: couponRow.minAnnuityDuration,
            maxAnnuityDuration: couponRow.maxAnnuityDuration
        };
        if (couponRow.percentsMap) {
            const { THINGS, PURITY, CARS } = couponRow.percentsMap;

            if (THINGS) {
                row.baseThings = THINGS.base || THINGS.baseAnnuity;
                row.delayThings = THINGS.delay || THINGS.delayAnnuity;
                row.baseAnnuityThings = isHybridCoupon ? THINGS.baseAnnuity : null;
                row.delayAnnuityThings = isHybridCoupon ? THINGS.delayAnnuity : null;
            }
            if (PURITY) {
                row.basePurity = PURITY.base || PURITY.baseAnnuity;
                row.delayPurity = PURITY.delay || PURITY.delayAnnuity;
                row.baseAnnuityPurity = isHybridCoupon ? PURITY.baseAnnuity : null;
                row.delayAnnuityPurity = isHybridCoupon ? PURITY.delayAnnuity : null;
            }
            if (CARS) {
                row.baseCars = CARS.base || CARS.baseAnnuity;
                row.delayCars = CARS.delay || CARS.delayAnnuity;
                row.baseAnnuityCars = isHybridCoupon ? CARS.baseAnnuity : null;
                row.delayAnnuityCars = isHybridCoupon ? CARS.delayAnnuity : null;
            }
        }
        return row;
    }, getRowsToExtend(baseCoupon) {
        let rows = [];
        if (baseCoupon) {
            rows = baseCoupon.rows.map(methods.convertCouponRow);
        }
        return rows;
    },
};

const CouponModal = ({initialValue = null, cashBoxId, onSuccess, close, flag, modalMode, isAnnuity}) => {
    const [error, setError] = useState('');
    const {accessFeatures} = useSelector(({user}) => user);
    const { ref, scrollToTop } = useScrollToTop();
    const showAnnuityHybrid = accessFeatures['deal.annuity.hybrid']

    let rows = emptyRows;
    let cashBoxesArr = [];
    const isEdit = initialValue && initialValue.length > 0 && modalMode !== 'copy';
    if (typeof cashBoxId === 'object') {
        cashBoxesArr = cashBoxId;
    }
    const [form, setForm] = useState(null)
    const [state, setState] = useState(() => {
        let data = {};
        if (initialValue && initialValue.length > 0) {
            if (initialValue.length === 1) {
                let rows = [];
                let coupon = initialValue[0];
                if (coupon.couponRowList && coupon.couponRowList.length > 0) {
                    const isHybridCoupon = coupon.couponType === couponType.ANNUITY_HYBRID
                    rows = coupon.couponRowList.map((row) => methods.convertCouponRow(row, isHybridCoupon));
                }

                data = {
                    couponName: coupon.name,
                    active: coupon.active,
                    startDate: coupon.startDate,
                    endDate: coupon.endDate,
                    minDuration: coupon.minDuration,
                    maxDuration: coupon.maxDuration,
                    category: defaultCategories.filter(c => c.value === coupon.category)[0],
                    purity: coupon.type.includes('PURITY'),
                    things: coupon.type.includes('THINGS'),
                    cars: coupon.type.includes('CARS'),
                    rows,
                    withoutRecalculation: coupon.withoutRecalculation,
                    percentFromAmount: coupon.percentFromAmount === 0 ? "" : coupon.percentFromAmount,
                    discountBirthday: coupon.discountBirthday,
                    discountBonus: coupon.discountBonus,
                    timeLimit: coupon.timeLimit,
                    timeFrom: coupon.timeFrom,
                    timeTo: coupon.timeTo,
                    couponType: coupon.couponType ?? couponAnnuityType.SHORT_TERM,
                };
                if (coupon.endDate < startOfToday) data.active = false
            }
        }
        return {
            rows: data.rows || rows,
            cashBoxes: cashBoxesArr,
            things: data.things !== false,
            purity: data.purity !== false,
            cars: data.cars !== false,
            active: !!data.active,
            startDate: data.startDate || '',
            endDate: data.endDate || '',
            category: data.category,
            minDuration: data.minDuration || data.minDuration === 0 ? data.minDuration : 3,
            maxDuration: data.maxDuration || 30,
            couponName: data.couponName || '',
            withoutRecalculation: !!data.withoutRecalculation,
            percentFromAmount: data.percentFromAmount,
            errMsg: '',
            discountBirthday: data.discountBirthday,
            discountBonus: data.discountBonus,
            timeLimit: data.timeLimit,
            timeFrom: data.timeLimit ? data.timeFrom : null,
            timeTo:  data.timeLimit ? data.timeTo : null,
            couponType: couponAnnuityType[data.couponType],
        };
    });
    const calculationCouponType = getCouponType(state.couponType)
    const isAnnuityCoupon = calculationCouponType  === couponType.ANNUITY
    const isHybridCoupon = calculationCouponType  === couponType.ANNUITY_HYBRID

    const handleSubmit = (entity) => {
        const couponType = getCouponType(state.couponType)
        let promise;
        let data = {
            couponName: state.couponName,
            purity: state.purity,
            things: state.things,
            active: state.active,
            startDate: entity.startDate,
            endDate: entity.endDate,
            minDuration: entity.minDuration,
            maxDuration: entity.maxDuration,
            withoutRecalculation: entity.withoutRecalculation,
            couponRows: entity.rows.map(el => ({
                startPrice: el.startPrice,
                endPrice: el.endPrice,
                couponRowName: el.couponRowName,
                percentsMap: createPercentsMap(el, couponType),
                minAnnuityDuration: el.minAnnuityDuration,
                maxAnnuityDuration: el.maxAnnuityDuration,
                basePercentBirthday: el.basePercentBirthday,
                basePercentBonus: el.basePercentBonus,
                baseAnnuityPercentBirthday: el.baseAnnuityPercentBirthday,
                baseAnnuityPercentBonus: el.baseAnnuityPercentBonus
            })),
            percentFromAmount: entity.percentFromAmount,
            discountBirthday: state.discountBirthday,
            discountBonus: state.discountBonus,
            timeLimit: state.timeLimit,
            timeFrom: state.timeFrom,
            timeTo: state.timeTo,
            couponType: calculationCouponType,
        }
        if (accessFeatures["cars.feature"]) {
            data.cars = state.cars;
        }
        if (accessFeatures["cashboxNameTypeOne"] && !entity.percentFromAmount) {
            data.percentFromAmount = 1;
        }
        if (accessFeatures["client.category"]) {
            data.category = entity?.category?.value === 0 ? '' : entity?.category?.value;
        }
        if (!accessFeatures["cashboxNameTypeOne"] && !entity.percentFromAmount) {
            data.percentFromAmount = 0;
        }
        if (initialValue && initialValue.length > 0 && modalMode !== 'copy') {
            if (initialValue.length > 1) {
                data.couponName = 'temp';

                promise = cashBoxService.updateCoupons(
                    cashBoxId,
                    initialValue.map((c) => c.id).join(','),
                    data,
                );
            } else {
                promise = cashBoxService.updateCoupon(cashBoxId, initialValue[0].id, data);
            }
        } else if (!isNaN(cashBoxId)) {
            promise = cashBoxService.createCoupon(cashBoxId, data);
        } else {
            let cashBoxesId = state.cashBoxes.map((c) => c.id).join(',');

            promise = cashBoxService.createCouponList(cashBoxesId, data);
        }

        promise.then(() => {
            if (onSuccess) onSuccess();
            close()
        })
            .catch(error => {
                if (error.response) {
                    let errorData = error.response.data
                    let commonError = messages["0"]
                    if (errorData.errorCode) {
                        commonError = messages[errorData.errorCode]
                    } else if (error.response.data.message === 'Validation error') {
                        commonError = messages[16205]
                        scrollToTop()
                    }
                    throw setError(commonError)
                }
            })
    };

    const createPercentsMap = (el, coupon) => {
        const isAnnuity = coupon === couponType.ANNUITY
        const isHybrid = coupon === couponType.ANNUITY_HYBRID
        const createTypeMap = (baseValue, delayValue, baseAnnuity, delayAnnuity) => ({
            base: isAnnuity ? null : baseValue,
            delay: isAnnuity ? null : delayValue,
            baseAnnuity: isAnnuity ? baseValue : isHybrid ? baseAnnuity : null,
            delayAnnuity: isAnnuity ? delayValue : isHybrid ? delayAnnuity : null
        });
    
        const percentsMap = {};
    
        if (state.purity) {
            percentsMap.PURITY = createTypeMap(el.basePurity, el.delayPurity, el.baseAnnuityPurity, el.delayAnnuityPurity);
        }
        if (state.cars) {
            percentsMap.CARS = createTypeMap(el.baseCars, el.delayCars, el.baseAnnuityCars, el.delayAnnuityCars);
        }
        if (state.things) {
            percentsMap.THINGS = createTypeMap(el.baseThings, el.delayThings, el.baseAnnuityThings, el.delayAnnuityThings);
        }
    
        return percentsMap;
    };

    const handleOnDelete = (index) => {
        let cashBoxes = state.cashBoxes;
        if (cashBoxes.length > 1) {
            cashBoxes.splice(index, 1);
            setState((prev) => ({...prev, cashBoxes}));
        }
    };

    const onEntityChange = (rows) => {
        setState((prev) => ({...prev, rows}));
    };

    const onInputChange = (event) => {
        const {name, checked, type, value} = event.target;
        const itemValue = type === 'checkbox' ? checked : value;

        setState((prev) => {
            const newState = { ...prev, [name]: itemValue };
        
            if (name === "couponType") {
              const newCouponType = getCouponType(itemValue);
              newState.minDuration = newCouponType !== couponType.ANNUITY ? 0 : 3;
              newState.maxDuration = newCouponType !== couponType.ANNUITY ? 30 : 25;
            }
        
            return newState;
          });
    };
    const handleChangeCategory = (values) => {
        setState((prev) => ({...prev, category: values}));
    }

    useEffect(() => {
        if(form) {
            form.setValue('minDuration', state.category?.value === 10 ? 0 : state.minDuration)
            form.setValue('maxDuration', state.category?.value === 10 ? 1 : state.maxDuration)
        }
    }, [state.category?.value, state.minDuration, state.maxDuration])

    return (<ReactModal
        isOpen={flag}
        contentLabel="Modal"
        className="modal-container coupon-modal"
        overlayClassName="modal-overlay">
        <HookForm onSubmit={handleSubmit}
                  validationSchema={createCouponScheme(accessFeatures["cars.feature"],
                      accessFeatures["client.category"],
                      accessFeatures["coupon.rows.name"],
                      state.discountBonus,
                      state.discountBirthday,
                      state.timeLimit,
                      isHybridCoupon,
                      isAnnuityCoupon)}
                  defaultValues={state} submitOnEnterPress={true}
                  predef={(methods) => {
                    if (!form) {
                        setForm(methods)
                    }
                }}>
            <div className="modal-header" ref={ref}>
                <h2>{isEdit ? 'Редактирование купона' : 'Создание нового купона'}</h2>
                <button className="button-icontrigger modal-closetrigger" onClick={close}>
            <span className="icon">
              <CrossIcon/>
            </span>
                </button>
            </div>
            {error && <Message type="error" text={error}/>}
            <div className="modal-body">
                {initialValue && initialValue.length > 1 && (<Message
                    type={'warning'}
                    text={'Выбрано несколько купонов.' + ' Информация по купону заполнена значениями по умолчанию.'}
                />)}
                <fieldset>
                    {state.cashBoxes && state.cashBoxes.length > 0 && (<React.Fragment>
                        <div className="content-section-title">
                            <h3>Кредитный продукт</h3>
                        </div>
                        <div className="form-element">
                            <label className="form-element-label" htmlFor="newCouponName">
                                Подразделения
                            </label>
                            <div className="form-element-data">
                                <SearchFacets
                                    reset={false}
                                    notCrossed={true}
                                    onDelete={handleOnDelete}
                                    dataPillboxes={state.cashBoxes.map((c) => c.name)}
                                />
                            </div>
                        </div>
                    </React.Fragment>)}
                    <div className="content-section-title">
                        <h3>Персональные данные</h3>
                    </div>
                    <div className="form-row">
                        <div className="form-row-item">
                            <div className="form-element">
                                <label className="form-element-label" htmlFor="newCouponName">
                                    Название
                                </label>
                                <div className="form-element-data">
                                    {initialValue && initialValue.length > 1 ? (<SearchFacets
                                        reset={false}
                                        notCrossed={true}
                                        dataPillboxes={initialValue.map((c) => c.name)}
                                    />) : (<HInput
                                        h="true"
                                        className="input"
                                        type="text"
                                        name="couponName"
                                        id="couponName"
                                        value={state.couponName}
                                        onChange={onInputChange}
                                    />)}
                                </div>
                            </div>
                        </div>
                        <div className="form-row-item">
                            <div className="form-element">
                                <label className="form-element-label" htmlFor="">
                                    Наименование залога
                                </label>
                                <div className="form-element-data">
                                    <div className="form-element-data-checkboxes">
                                        {accessFeatures["cars.feature"] && <HCheckbox
                                            label={'Автомобили'}
                                            id={'cars'}
                                            name={'cars'}
                                            h="true"
                                            defaultValue={state.cars}
                                            onChange={onInputChange}
                                        />}
                                        <HCheckbox
                                            label={'Вещи'}
                                            id={'things'}
                                            name={'things'}
                                            h="true"
                                            defaultValue={state.things}
                                            onChange={onInputChange}
                                        />
                                        <HCheckbox
                                            label={'Драгоценные металлы'}
                                            id={'purity'}
                                            name={'purity'}
                                            h="true"
                                            defaultValue={state.purity}
                                            onChange={onInputChange}
                                        />
                                        <span className="form-message error">{state.errMsg}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        {<div className="form-row-item">
                            <div className="form-element">
                                <label className="form-element-label" htmlFor="couponType">
                                    Тип залога
                                </label>
                                <div className="form-element-data">
                                    <div className="form-element-data-checkboxes">
                                    <HRadio
                                        h="true"
                                        labels={generateCouponTypes(showAnnuityHybrid)}
                                        name='couponType'
                                        checkboxStyle={true}
                                        onChange={onInputChange}
                                        defaultCheckedLabel={state.couponType || couponAnnuityType.SHORT_TERM}
                                    />
                                    </div>
                                </div>
                            </div>
                        </div>}
                        <div className="form-row-item">
                            <div className="form-element">
                                <label className="form-element-label" htmlFor="">
                                    Статус
                                </label>
                                <div className="form-element-data">
                                    <div className="form-element-data-checkboxes">
                                        <HCheckbox
                                            label={'Активен'}
                                            id={'active'}
                                            name={'active'}
                                            h="true"
                                            defaultValue={state.active}
                                            onChange={onInputChange}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="form-row-item">
                            <div className="form-element">
                                <label className="form-element-label" htmlFor="">
                                    Расчёт договора
                                </label>
                                <div className="form-element-data">
                                    <div className="form-element-data-checkboxes">
                                        <HCheckbox
                                            label="Без перерасчёта"
                                            id="withoutRecalculation"
                                            name="withoutRecalculation"
                                            h="true"
                                            defaultValue={state.withoutRecalculation}
                                            onChange={onInputChange}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        {accessFeatures['coupons.bonus.enable'] && (<div className="coupon-modal-item">
                            <label className="form-element-label coupon-modal-label"
                                   htmlFor="discountBonus">
                                Бонус
                            </label>
                            <div className="form-element-data-checkboxes">
                                <HCheckbox
                                    id="discountBonus"
                                    name="discountBonus"
                                    h="true"
                                    defaultValue={state.discountBonus}
                                    onChange={onInputChange}
                                />
                            </div>
                        </div>)}
                        {accessFeatures['coupons.birthday.enable'] && <div className="coupon-modal-item">
                            <label className="form-element-label coupon-modal-label"
                                   htmlFor="discountBirthday">
                                Праздничный
                            </label>
                            <div className="form-element-data-checkboxes">
                                <HCheckbox
                                    id="discountBirthday"
                                    name="discountBirthday"
                                    h="true"
                                    defaultValue={state.discountBirthday}
                                    onChange={onInputChange}
                                />
                            </div>
                        </div>}
                        {
                            accessFeatures["client.category"] &&
                            <div className="coupon-modal-item">
                                <label className="form-element-label coupon-modal-label"
                                       htmlFor="timeLimit">
                                    Часы действия купона
                                </label>
                                <div className="form-element-data-checkboxes">
                                    <HCheckbox
                                        id="timeLimit"
                                        name="timeLimit"
                                        h="true"
                                        defaultValue={state.timeLimit}
                                        onChange={onInputChange}
                                    />
                                </div>
                            </div>
                        }
                    </div>
                    <div className="form-row">
                        <div className="form-row-item">
                            <div className="form-element">
                                <label className="form-element-label" htmlFor="startDate">
                                    Дата начала действия
                                </label>
                                <div className="form-element-data">
                                    <HDatePicker
                                        h="true"
                                        name="startDate"
                                        defaultValue={state.startDate}
                                        onBlur={onInputChange}
                                        className="input"
                                        id="startDate"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="form-row-item">
                            <div className="form-element">
                                <label className="form-element-label" htmlFor="endDate">
                                    Дата окончания действия
                                </label>
                                <div className="form-element-data">
                                    <HDatePicker
                                        h="true"
                                        name="endDate"
                                        defaultValue={state.endDate}
                                        onBlur={onInputChange}
                                        className="input"
                                        id="endDate"
                                    />
                                </div>
                            </div>
                        </div>
                        {accessFeatures["client.category"] && <div className="form-row-item">
                            <div className="form-element">
                                <label className="form-element-label" htmlFor="category">
                                    Доступен для категории
                                </label>
                                <div className="form-element-data combobox-md">
                                    <DSelect
                                        h="true"
                                        name={`category`}
                                        maxMenuHeight={200}
                                        defaultValue={!state.category ? defaultCategories[0] : state.category}
                                        options={defaultCategories}
                                        handleOnChange={handleChangeCategory}
                                        placeholder="Категория"
                                    />
                                </div>
                            </div>
                        </div>}
                    </div>
                    <div className="form-row">
                        <div className="form-row-item">
                            <div className="form-element">
                                <label className="form-element-label" htmlFor="minDuration">
                                    Минимальный срок микрозайма
                                </label>
                                <div className="form-element-data number">
                                    <HInput
                                        h="true"
                                        className="input"
                                        type="number"
                                        name={`minDuration`}
                                        id={`minDuration`}
                                        onChange={onInputChange}
                                        value={state.minDuration}
                                    />
                                    {isAnnuityCoupon && <span>мес.</span>}
                                </div>
                            </div>
                        </div>
                        <div className="form-row-item">
                            <div className="form-element">
                                <label className="form-element-label" htmlFor="maxDuration">
                                    Максимальный срок микрозайма
                                </label>
                                <div className="form-element-data number">
                                    <HInput
                                        h="true"
                                        className="input"
                                        type="number"
                                        name={`maxDuration`}
                                        id={`maxDuration`}
                                        onChange={onInputChange}
                                        value={state.maxDuration}
                                    />
                                    {isAnnuityCoupon && <span>мес.</span>}
                                </div>
                            </div>
                        </div>
                        <div className="form-row-item">
                            <div className="form-element">
                                <label className="form-element-label" htmlFor="maxDuration">
                                    Процент от оценки
                                </label>
                                <div className="form-element-data number">
                                    <HInput
                                        h="true"
                                        className="input"
                                        type="text"
                                        name={`percentFromAmount`}
                                        id={`percentFromAmount`}
                                        value={state.category?.value === 10 ? 0.7 : state.percentFromAmount}
                                        onChange={onInputChange}
                                        decimals={2}
                                    />
                                </div>
                            </div>
                        </div>
                        {state.timeLimit && <div className='coupon-modal-timeLimit'>
                            <div className="form-element-data number">
                                <label className="form-element-label" htmlFor="maxDuration">
                                    Время действия от
                                </label>
                                <HInput
                                    h="true"
                                    className="input"
                                    type="time"
                                    name='timeFrom'
                                    id='timeFrom'
                                    value={state.timeFrom}
                                    onChange={onInputChange}
                                />
                            </div>
                            <div className="form-element-data number coupon-modal-timeLimitItem">
                                <label className="form-element-label" htmlFor="maxDuration">
                                    Время действия до
                                </label>
                                <HInput
                                    h="true"
                                    className="input"
                                    type="time"
                                    name='timeTo'
                                    id='timeTo'
                                    value={state.timeTo}
                                    onChange={onInputChange}
                                />
                            </div>
                        </div>}
                    </div>
                </fieldset>
                <CouponRowsTable
                    h="true"
                    isDiscountBirthday={state.discountBirthday}
                    isDiscountBonus={state.discountBonus}
                    isThings={state.things}
                    isPurity={state.purity}
                    isCars={state.cars}
                    rows={state.rows}
                    coupon={state.couponType}
                    onEntityChange={onEntityChange}
                    onInputChange={onEntityChange}
                />
            </div>
            <div className="modal-footer">
                <button className="button">Сохранить</button>
                <button className="button-light" onClick={close}>
                    Отменить
                </button>
            </div>
        </HookForm>
    </ReactModal>);
};

export default CouponModal;
